/*
  This module is the serializable "data" layer for transcript paragraph/subtitle
  breaking rules. Every field here is JSON-friendly, so these settings are what
  gets stored in tenant config, sent over the wire, and migrated across schema
  versions. The module deliberately has no imports, letting lightweight
  consumers (e.g. server transcription utils, tenant config) depend on it without
  pulling in the runtime behavior layer.

  The executable counterpart is `TranscriptParametricRule` in
  `./transcriptParametricRule`, which enriches these settings with in-memory
  evaluation/formatting callbacks. The data layer (this file) never depends on
  the behavior layer; the dependency only runs the other way. Settings are turned
  into runtime rules by `getParametricRules` in `./transcriptParametricRules`.
*/

interface TranscriptRuleSettingBase {
  property: string;
  label: string;
  description?: string;
  isActive: boolean;
}

/** Serializable config for a rule backed by a numeric threshold (rendered as a slider). */
export type TranscriptSliderRuleSetting = TranscriptRuleSettingBase & {
  value: number;
  min: number;
  max: number;
};

/*
  Serializable config for a rule that acts as a pure toggle.
  Slider-specific fields are explicitly disallowed so the union remains meaningful.
*/
export type TranscriptBooleanRuleSetting = TranscriptRuleSettingBase & {
  value?: never;
  min?: never;
  max?: never;
};

/**
 * Serializable rule setting used to configure transcript paragraph/subtitle
 * breaking behavior. This is the persisted/transported data shape; its
 * runtime, executable counterpart is `TranscriptParametricRule`.
 */
export type TranscriptRuleSetting = TranscriptSliderRuleSetting | TranscriptBooleanRuleSetting;

/**
 * Returns whether a transcript rule setting includes slider fields (`value`, `min`, and `max`).
 */
export function isTranscriptSliderRuleSetting(setting: TranscriptRuleSetting): setting is TranscriptSliderRuleSetting {
  if (!('value' in setting) || !('min' in setting) || !('max' in setting)) {
    return false;
  }

  return typeof setting.value === 'number' && typeof setting.min === 'number' && typeof setting.max === 'number';
}
